<template>
  <button
    type="button"
    class="wifi-btn"
    :class="{
      warning: connectionStatus === 'warning',
      error: connectionStatus === 'error',
    }"
    :aria-label="'connection'"
    @click="emitter.emit('showConnectionManagement')"
  >
    <UIcon v-if="connectionStatus === 'error'" name="i-lucide-wifi-off" class="wifi-icon" />
    <UIcon v-else name="i-lucide-wifi" class="wifi-icon" />
  </button>
</template>

<script setup lang="ts">
import { Ref, computed, inject, onMounted, ref } from 'vue';
import { Emitter } from '@/utilities/emitter';
import { useAppStore } from '@/stores/useAppStore'
import { Terminal } from 'app-core';

const store = useAppStore()
const emitter = inject('emitter') as Emitter

const terminals: Ref<Terminal[]> = ref([])
const isWeb = computed(() => store.device?.platform === 'web')

onMounted(async () => {
  terminals.value = Terminal.all()
})

const showLocalConnection = computed(() => !isWeb.value && !!store.terminal && terminals.value.some(t => t.isKitchenDisplaySystem() || t.isOrderingDevice()))
const hasConnectedTerminals = computed(() => store.localConnection.terminals.filter(t => t.connected).length > 1)
const connectionsStatus = computed(() => {
  return {
    internet: store.isOnline ? 'success' : 'error',
    local: !store.localConnection.connected
      ? 'error'
      : (store.localConnection.isServer && !hasConnectedTerminals.value ? 'warning' : 'success')
  }
})

const connectionStatus = computed(() => {
  const { local, internet } = connectionsStatus.value
  if(!showLocalConnection.value) return internet

  if(internet === 'success' && local === 'success') return 'success'
  else if(internet === 'success') return 'warning'

  return 'error'
})
</script>

<style lang="scss" scoped>
/* Connection status (owner ruling 2026-07-10): healthy is a plain green
 * glyph on transparent — only the attention states paint the solid status
 * circle (white icon, soft glow) and blink: amber 1.2s / red 0.9s.
 * In screen headers (sales, KDS, login) the glyph is pinned to the header
 * icon size, 20px in a 44px target, so it matches the icon buttons beside
 * it. Only the desktop rail (`rail-status`) scales with viewport height so
 * the rail never clips on short screens (full 44px until ~720px tall). */
.wifi-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  height: 44px;
  width: 44px;
  color: var(--ui-success);
  background: transparent;
  cursor: pointer;

  .wifi-icon {
    width: 20px;
    height: 20px;
  }

  &.rail-status {
    height: clamp(34px, 6.1vh, 44px);
    width: clamp(34px, 6.1vh, 44px);

    .wifi-icon {
      width: clamp(15px, 2.78vh, 20px);
      height: clamp(15px, 2.78vh, 20px);
    }
  }

  &.warning {
    --wifi-attention: var(--app-rail-attention);
    color: #ffffff; // white-on-solid
    background: var(--wifi-attention);
    box-shadow: 0 6px 16px color-mix(in srgb, var(--wifi-attention) 40%, transparent);
    animation: konty-blink 1.2s ease-in-out infinite;
  }

  &.error {
    --wifi-attention: var(--ui-error);
    color: #ffffff; // white-on-solid
    background: var(--wifi-attention);
    box-shadow: 0 6px 16px color-mix(in srgb, var(--wifi-attention) 40%, transparent);
    animation: konty-blink 0.9s ease-in-out infinite;
  }

  // Phones: the attention circle is drawn at 32px inside the same 44px
  // target, so it no longer dominates the compact header.
  @media (max-width: 767px) {
    &:not(.rail-status).warning,
    &:not(.rail-status).error {
      position: relative;
      isolation: isolate;
      background: transparent;
      box-shadow: none;

      &::before {
        content: '';
        position: absolute;
        inset: 6px;
        z-index: -1;
        border-radius: 50%;
        background: var(--wifi-attention);
        box-shadow: 0 4px 10px color-mix(in srgb, var(--wifi-attention) 40%, transparent);
      }

      .wifi-icon {
        width: 18px;
        height: 18px;
      }
    }
  }

  @keyframes konty-blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.35; }
  }
}
</style>
